import {useContext, useEffect, useRef, useState} from 'react';
import {shuffle} from 'lodash';
import { config } from '../config';
import {MapViewContext, TileCoord} from "../contexts/MapViewContext";
import FeedItemCard from "./FeedItemCard";
import './FeedSidebar.css';
import CategoryFilter from "./CategoryFilter";
import {GiHamburgerMenu} from "react-icons/gi";
import {RxHamburgerMenu} from "react-icons/rx";
import SearchFilter from "./SearchFilter";
import {TagCountsGrouped} from "../models/TagCountsGrouped";
import SelectedFilters from "./SelectedFilters";

const TAG_COUNTS_CACHE_KEY = 'tagCountsGroupedCache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes


type CachedEntry = {
    timestamp: number;
    data: TagCountsGrouped;
};

type CacheStore = Record<string, CachedEntry>;

const FeedSidebar = ({mapId}) => {
    const ITEM_HEIGHT = 430;
    const BUFFER_ITEMS = 2;

    const {
        mapTileCoords,
        viewportBounds,
        mapName,
        selectedTagIds,
        selectedCategoryIds,
        sidebarCollapsed,
        setSidebarCollapsed,
        searchQuery
    } = useContext(MapViewContext);
    const [items, setItems] = useState([]);
    const [categoryCounts, setCategoryCounts] = useState<TagCountsGrouped>({});
    const [offset, setOffset] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const feedRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) {
                setSidebarCollapsed(true);
            } else {
                setSidebarCollapsed(false);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (mapTileCoords.length === 0) return;

        let isCancelled = false;
        setOffset(0);
        setHasMore(true);
        setIsLoadingMore(false);

        (async () => {
            const newItems = await fetchFeedItems(0);
            if (!isCancelled) setItems(newItems);
        })();

        fetchTagCountsGrouped().then(data => {
            if (!isCancelled) setCategoryCounts(data);
        });

        return () => {
            isCancelled = true;
        };
    }, [mapId, mapTileCoords, viewportBounds, selectedTagIds, selectedCategoryIds, searchQuery]);

    useEffect(() => {
        const el = feedRef.current;
        if (!el) return;

        const handleScroll = () => {
            if (isLoadingMore || !hasMore) return;
            const threshold = ITEM_HEIGHT * BUFFER_ITEMS;
            const scrollPosition = el.scrollTop + el.clientHeight;
            const totalHeight = el.scrollHeight;
            if (totalHeight - scrollPosition <= threshold) {
                setIsLoadingMore(true);
                const nextOffset = offset + 30;

                fetchFeedItems(nextOffset).then(newItems => {
                    if (newItems.length === 0) {
                        setHasMore(false);
                        setIsLoadingMore(false);
                        return;
                    }
                    setItems(prev => {
                        const all = [...prev, ...newItems];
                        const deduped = Array.from(new Map(all.map(i => [i.state_id, i])).values());
                        return shuffle(deduped);
                    });
                    setOffset(nextOffset);
                    setIsLoadingMore(false);
                });
            }
        };

        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [offset, isLoadingMore, hasMore, selectedTagIds, mapTileCoords, viewportBounds, selectedCategoryIds, searchQuery]);


    const fetchFeedItems = async (offset: number) => {
        const uniqueTileCoords = (tiles: TileCoord[]): TileCoord[] => {
            const seen = new Set<string>();
            return tiles.filter(({z, x, y}) => {
                const key = `${z}-${x}-${y}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        };
        const params = new URLSearchParams();
        if (selectedTagIds.length > 0) {
            params.set('tag_ids', selectedTagIds.join(','));
        }

        if (selectedCategoryIds.length > 0) {
            params.set('categories_ids', selectedCategoryIds.join(','));
        }

        if (searchQuery !== null && searchQuery.length > 3) {
            params.set('search', searchQuery);
        }

        const feedResults = await Promise.all(
            uniqueTileCoords(mapTileCoords).map(({z, x, y}) =>
                fetch(`${config.directoryMapFeedUrl}/feed/${mapId}/${z}/${x}/${y}/${offset}?${params.toString()}`)
                    .then(res => res.json())
                    .catch(() => [])
            )
        );
        const bounds = mapTileCoords[0].bounds;
        const allItems = feedResults.flat();
        const deduped = Array.from(new Map(allItems.map(i => [i.state_id, i])).values())
            .filter(item =>
                item.space_status !== 'Unoccupied' &&
                item.media_keys !== null &&
                item.geom?.coordinates &&
                viewportBounds.contains([item.geom.coordinates[0], item.geom.coordinates[1]])
            );

        const counts: Record<string, { name: string; count: number }> = {};
        for (const item of deduped) {

            const id = item.categories_primary_id;
            const name = item.categories_primary_name || 'Unknown';

            if (!id) continue;
            if (!counts[id]) {
                counts[id] = {name, count: 1};
            } else {
                counts[id].count += 1;
            }
        }

        return shuffle(deduped);
    };

    const getCacheKey = (uniqueTiles: TileCoord[]) => {
        const sortedTiles = uniqueTiles.sort((a, b) => {
            if (a.z !== b.z) return a.z - b.z;
            if (a.x !== b.x) return a.x - b.x;
            return a.y - b.y;
        });
        return `${mapId}:${sortedTiles.map(t => `${t.z}-${t.x}-${t.y}`).join('|')}`;
    }

    const fetchTagCountsGrouped = async (): Promise<TagCountsGrouped> => {
        const uniqueTileCoords = (tiles: TileCoord[]): TileCoord[] => {
            const seen = new Set<string>();
            return tiles.filter(({z, x, y}) => {
                const key = `${z}-${x}-${y}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        };
        const uniqueTiles = uniqueTileCoords(mapTileCoords);
        const cacheKey = getCacheKey(uniqueTiles);
        // Check localStorage
        const cacheStoreRaw = localStorage.getItem(TAG_COUNTS_CACHE_KEY);
        let cacheStore: CacheStore = {};
        if (cacheStoreRaw) {
            try {
                cacheStore = JSON.parse(cacheStoreRaw);
                const entry = cacheStore[cacheKey];
                if (entry && Date.now() - entry.timestamp < CACHE_TTL_MS) {
                    return entry.data;
                }
            } catch (err) {
                console.warn('Failed to parse tag counts cache store:', err);
            }
        }


        const tileCountResults = await Promise.all(
            uniqueTiles.map(({z, x, y}) =>
                fetch(`${config.directoryMapFeedUrl}/tile-counts/${mapId}/${z}/${x}/${y}`)
                    .then(res => res.json())
                    .catch(() => [])
            )
        );

        const flatResults = tileCountResults.flat();
        const grouped: TagCountsGrouped = {};

        for (const row of flatResults) {
            const catId = row.categories_primary_id;
            const catName = row.categories_primary_name || 'Unknown';
            const tagId = row.tags_primary_id;
            const tagName = row.tags_primary_name || 'Unknown';
            const tagColor = row.color || '';
            const count = row.count;

            if (!catId || !tagId) continue;

            if (!grouped[catId]) {
                grouped[catId] = {
                    name: catName,
                    tags: {
                        [tagId]: {name: tagName, count, color: tagColor}
                    },
                };
            } else {
                const category = grouped[catId];
                if (!category.tags[tagId]) {
                    category.tags[tagId] = {name: tagName, count, color: tagColor};
                } else {
                    category.tags[tagId].count += count;
                }
            }
        }

        // Store in localStorage
        cacheStore[cacheKey] = {
            timestamp: Date.now(),
            data: grouped
        };
        localStorage.setItem(TAG_COUNTS_CACHE_KEY, JSON.stringify(cacheStore));

        return grouped;
    };

    return (
        <div className={`sidebar-wrapper ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-toggle" onClick={() => setSidebarCollapsed(prev => !prev)}>
                {sidebarCollapsed ? <RxHamburgerMenu/> : <GiHamburgerMenu/>}
            </div>
            {!sidebarCollapsed && (
                <>
                    <div className="sidebar-header">
                        <div className="sidebar-title">
                            <h5>{mapName}</h5>
                        </div>
                    </div>
                    <SearchFilter categoryCounts={categoryCounts}/>
                    <SelectedFilters categoryCounts={categoryCounts}/>
                    <CategoryFilter categoryCounts={categoryCounts} feedRef={feedRef}/>
                    <div className="sidebar-feed" ref={feedRef}>
                        {items.map((item) => (
                            <FeedItemCard key={item.state_id} item={item}/>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default FeedSidebar;
